import { useEffect, useId, useRef, useState } from "react";
import { Search } from "lucide-react";
import { formatPhone } from "../../api";
import { useApi } from "../../hooks";
import { contactName } from "../../crm";
import type { ContactRow } from "./types";

/** Select the contact record itself, including people who have no email address. */
export default function ContactPicker({ value, onChange, disabled }: {
  value: ContactRow | null;
  onChange: (contact: ContactRow | null) => void;
  disabled?: boolean;
}) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(true);
  const [active, setActive] = useState(-1);
  const contacts = useApi<ContactRow[]>(value ? null : `/api/crm/contacts?q=${encodeURIComponent(query)}`);
  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(search.trim()), 200);
    return () => window.clearTimeout(timer);
  }, [search]);
  const loading = contacts.loading || search.trim() !== query;
  const matches = loading || contacts.error ? [] : (contacts.data || []).slice(0, 8);
  useEffect(() => {
    if (expanded && active >= 0) document.getElementById(`${id}-contact-${active}`)?.scrollIntoView({ block: "nearest" });
  }, [active, expanded, id]);
  const optionId = (index: number) => `${id}-contact-${index}`;
  const choose = (contact: ContactRow) => { onChange(contact); setExpanded(false); setActive(-1); };
  /** Company, email and phone as separate quiet pieces (no "·" strings). */
  const details = (contact: ContactRow) => {
    const parts = [contact.company, contact.email, contact.phone ? formatPhone(contact.phone) : ""].filter(Boolean);
    return parts.length ? <span className="crm-contact-meta">{parts.map((p, i) => <span key={i}>{p}</span>)}</span> : null;
  };

  if (value) return (
    <div className="crm-contact-selected">
      <div role="status"><strong>{contactName(value)}</strong>{details(value)}</div>
      <button type="button" className="button secondary small" disabled={disabled} onClick={() => {
        onChange(null); setSearch(""); setQuery(""); setExpanded(true); setActive(-1);
        window.requestAnimationFrame(() => input.current?.focus());
      }}>Change contact</button>
    </div>
  );

  return (
    <div className="crm-contact-picker">
      <label htmlFor={`${id}-search`}>Contact</label>
      <div className="crm-contact-search">
        <Search aria-hidden="true" />
        <input ref={input} id={`${id}-search`} role="combobox" aria-expanded={expanded}
          aria-autocomplete="list" aria-controls={expanded ? `${id}-results` : undefined}
          aria-activedescendant={expanded && matches[active] ? optionId(active) : undefined}
          placeholder="Search by name, email or company" autoComplete="off" maxLength={100}
          disabled={disabled} value={search}
          onFocus={() => setExpanded(true)}
          onChange={(e) => { setSearch(e.target.value); setActive(-1); setExpanded(true); }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
              e.preventDefault(); setExpanded(true);
              if (matches.length) setActive((current) => current < 0
                ? e.key === "ArrowDown" ? 0 : matches.length - 1
                : (current + (e.key === "ArrowDown" ? 1 : -1) + matches.length) % matches.length);
            } else if (e.key === "Enter") {
              e.preventDefault();
              if (expanded && matches[active]) choose(matches[active]);
            } else if (e.key === "Escape" && expanded) {
              e.preventDefault(); e.stopPropagation(); setExpanded(false); setActive(-1);
            }
          }} />
      </div>
      {expanded && <div className="crm-contact-results">
        <div id={`${id}-results`} role="listbox" aria-label="Matching contacts" aria-busy={loading}>
          {matches.map((contact, index) => <button type="button" role="option" id={optionId(index)}
            key={contact.id} aria-selected={active === index} disabled={disabled}
            onMouseDown={(e) => e.preventDefault()} onClick={() => choose(contact)}>
            <strong>{contactName(contact)}</strong>{details(contact)}
          </button>)}
        </div>
        {/* Only says something when there is something to say: searching, nothing found, or more than shown. */}
        {(() => {
          const status = loading ? "Searching…"
            : contacts.error ? "Contacts could not be loaded."
            : !matches.length ? query ? "No match. Try another search, or add them under New contact." : "No contacts yet. Add one under New contact."
            : (contacts.data?.length || 0) > 8 ? "Type to find more." : "";
          return <p className="crm-muted" role="status" hidden={!status}>{status}</p>;
        })()}
        {contacts.error && <button type="button" className="button secondary small" onClick={contacts.reload}>Retry contacts</button>}
      </div>}
    </div>
  );
}
